import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Product Category interface
 */
export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  parent?: string | null;
  icon?: string;
  display_order: number;
  is_active: boolean;
  default_tax_rate?: string | null;
  depth_level: number;
  full_path: string;
  subcategories: ProductCategory[];
  created_at: string;
  updated_at: string;
}

/**
 * Central Product interface
 */
export interface CentralProduct {
  product_id: string;
  barcode: string;
  name: string;
  brand: string;
  category: string;
  category_name: string;
  category_path: string;
  base_unit: string;
  short_description?: string;
  description?: string;
  specifications?: Record<string, any>;
  certifications?: string[];
  primary_image_url?: string;
  additional_images?: string[];
  storage_temperature?: string;
  shelf_life_days?: number;
  country_of_origin?: string;
  status: "VERIFIED" | "PENDING" | "PENDING_VERIFICATION" | "REJECTED";
  is_verified: boolean;
  is_pending: boolean;
  is_rejected: boolean;
  verified_by_id?: string | null;
  verified_at?: string;
  rejection_reason?: string;
  total_suppliers: number;
  total_orders: number;
  created_at: string;
  updated_at: string;
}

/**
 * Request Product interface
 */
export interface RequestProduct {
  product_id: string;
  barcode: string;
  name: string;
  brand: string;
  category: string;
  category_name: string;
  base_unit: string;
  short_description?: string;
  primary_image_url?: string;
  status: "PENDING_VERIFICATION" | "VERIFIED" | "REJECTED";
  days_pending?: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

/**
 * Supplier Product interface
 */
export interface SupplierProduct {
  id: string;
  supplier_id: string;
  central_product: string;
  central_product_name: string;
  central_product_barcode: string;
  central_product_category: string;
  supplier_sku: string | null;
  supplier_product_name: string | null;
  effective_product_name: string;
  unit_price: string;
  currency: string;
  unit: string;
  pack_size: string;
  pack_unit: string | null;
  minimum_order_quantity: string;
  minimum_order_value: string | null;
  allows_below_minimum: boolean;
  below_minimum_message: string | null;
  is_available: boolean;
  stock_status: string;
  is_in_stock: boolean;
  is_low_stock: boolean;
  available_quantity: number | null;
  lead_time_days: number;
  is_featured: boolean;
  priority_rank: number;
  total_orders: number;
  total_quantity_sold: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

/**
 * Pagination interface
 */
export interface PaginationInfo {
  count: number;
  next: string | number | null;
  previous: string | number | null;
  total_pages?: number;
  current_page?: number;
}

/**
 * Product state interface
 */
export interface ProductState {
  // Product Categories
  categories: ProductCategory[];
  categoriesLoading: boolean;
  categoriesPagination: PaginationInfo;

  // Product Category CRUD states
  productCategories: ProductCategory[];
  productCategoryCreateEditLoading: boolean;
  categoryCreateEditError: string | null;
  productCategoryCreateEditFieldErrors: Record<string, string> | null;
  productCategoryDeleteLoading: {
    loading: boolean;
    deleteItemId?: string;
  };

  // Central Products
  centralProducts: CentralProduct[];
  centralProductsLoading: boolean;
  centralProductsPagination: PaginationInfo;

  // Request Products
  requestProducts: RequestProduct[];
  requestProductsLoading: boolean;
  requestProductsPagination: PaginationInfo;

  // Supplier Products
  supplierProducts: SupplierProduct[];
  supplierProductsLoading: boolean;
  supplierProductsPagination: PaginationInfo;

  // Selected items for edit operations
  selectedCategoryId: string | null;
  selectedCategory: ProductCategory | null;
  selectedProductId: string | null;
  selectedProduct: CentralProduct | null;
  selectedRequestId: string | null;
  selectedRequest: RequestProduct | null;
  selectedSupplierId: string | null;
  selectedSupplierProduct: SupplierProduct | null;

  // Dialog states
  categoryCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: ProductCategory;
  };

  productCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: CentralProduct;
  };

  requestCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: RequestProduct;
  };

  supplierProductCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: SupplierProduct;
  };

  // Delete dialog states
  isCategoryDeleteDialogOpen: {
    open: boolean;
    deleteItemId?: string;
  };

  isProductDeleteDialogOpen: {
    open: boolean;
    deleteItemId?: string;
  };

  isRequestDeleteDialogOpen: {
    open: boolean;
    deleteItemId?: string;
  };

  isSupplierProductDeleteDialogOpen: {
    open: boolean;
    deleteItemId?: string;
  };
}

const initialState: ProductState = {
  // Product Categories
  categories: [],
  categoriesLoading: false,
  categoriesPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Product Category CRUD states
  productCategories: [],
  productCategoryCreateEditLoading: false,
  categoryCreateEditError: null,
  productCategoryCreateEditFieldErrors: null,
  productCategoryDeleteLoading: {
    loading: false,
    deleteItemId: undefined,
  },

  // Central Products
  centralProducts: [],
  centralProductsLoading: false,
  centralProductsPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Request Products
  requestProducts: [],
  requestProductsLoading: false,
  requestProductsPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Supplier Products
  supplierProducts: [
    {
      id: "default-supplier-product-id",
      supplier_id: "default-supplier-id",
      central_product: "default-central-product-id",
      central_product_name: "Default Supplier Product",
      central_product_barcode: "1234567890",
      central_product_category: "Default Category",
      supplier_sku: null,
      supplier_product_name: null,
      effective_product_name: "Default Supplier Product",
      unit_price: "10.50",
      currency: "GBP",
      unit: "KG",
      pack_size: "1.00",
      pack_unit: null,
      minimum_order_quantity: "1.00",
      minimum_order_value: null,
      allows_below_minimum: false,
      below_minimum_message: null,
      is_available: true,
      stock_status: "IN_STOCK",
      is_in_stock: true,
      is_low_stock: false,
      available_quantity: 100,
      lead_time_days: 1,
      is_featured: false,
      priority_rank: 0,
      total_orders: 0,
      total_quantity_sold: "0.00",
      certifications: [],
      storage_temperature: "Room Temperature",
      shelf_life_days: 365,
      country_of_origin: "USA",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ],
  supplierProductsLoading: false,
  supplierProductsPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Selected items
  selectedCategoryId: null,
  selectedCategory: null,
  selectedProductId: null,
  selectedProduct: null,
  selectedRequestId: null,
  selectedRequest: null,
  selectedSupplierId: null,
  selectedSupplierProduct: null,

  // Dialog states
  categoryCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  productCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  requestCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  supplierProductCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  // Delete dialog states
  isCategoryDeleteDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  isProductDeleteDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  isRequestDeleteDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  isSupplierProductDeleteDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },
};

export const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    // Category actions
    setCategories: (state, action: PayloadAction<ProductCategory[]>) => {
      state.categories = action.payload;
      state.categoriesLoading = false;
    },
    setCategoriesLoading: (state, action: PayloadAction<boolean>) => {
      state.categoriesLoading = action.payload;
    },
    setCategoriesPagination: (state, action: PayloadAction<PaginationInfo>) => {
      state.categoriesPagination = action.payload;
    },
    setSelectedCategoryId: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
    },
    clearSelectedCategoryId: (state) => {
      state.selectedCategoryId = null;
    },
    setSelectedCategory: (
      state,
      action: PayloadAction<ProductCategory | null>,
    ) => {
      state.selectedCategory = action.payload;
    },
    setCategoryCreateEditDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        openFor: "create" | "edit" | "view";
        editItemData?: ProductCategory;
      }>,
    ) => {
      state.categoryCreateEditDialogOpen = action.payload;
    },
    clearCategoryCreateEditDialogOpen: (state) => {
      state.categoryCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },
    setIsCategoryDeleteDialogOpen: (
      state,
      action: PayloadAction<{ open: boolean; deleteItemId?: string }>,
    ) => {
      state.isCategoryDeleteDialogOpen = action.payload;
    },

    setProductCategoryCreateEditLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.productCategoryCreateEditLoading = action.payload;
    },
    setCategoryCreateEditError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.categoryCreateEditError = action.payload;
    },
    setProductCategoryCreateEditFieldErrors: (
      state,
      action: PayloadAction<Record<string, string> | null>,
    ) => {
      state.productCategoryCreateEditFieldErrors = action.payload;
    },
    setProductCategoryDeleteLoading: (
      state,
      action: PayloadAction<{ loading: boolean; deleteItemId?: string }>,
    ) => {
      state.productCategoryDeleteLoading = action.payload;
    },

    // Central Product actions
    setCentralProducts: (state, action: PayloadAction<CentralProduct[]>) => {
      state.centralProducts = action.payload;
      state.centralProductsLoading = false;
    },
    setCentralProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.centralProductsLoading = action.payload;
    },
    setCentralProductsPagination: (
      state,
      action: PayloadAction<PaginationInfo>,
    ) => {
      state.centralProductsPagination = action.payload;
    },
    setSelectedProductId: (state, action: PayloadAction<string | null>) => {
      state.selectedProductId = action.payload;
    },
    clearSelectedProductId: (state) => {
      state.selectedProductId = null;
    },
    setSelectedProduct: (
      state,
      action: PayloadAction<CentralProduct | null>,
    ) => {
      state.selectedProduct = action.payload;
    },
    setProductCreateEditDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        openFor: "create" | "edit" | "view";
        editItemData?: CentralProduct;
      }>,
    ) => {
      state.productCreateEditDialogOpen = action.payload;
    },
    clearProductCreateEditDialogOpen: (state) => {
      state.productCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },
    setIsProductDeleteDialogOpen: (
      state,
      action: PayloadAction<{ open: boolean; deleteItemId?: string }>,
    ) => {
      state.isProductDeleteDialogOpen = action.payload;
    },

    // Request Product actions
    setRequestProducts: (state, action: PayloadAction<RequestProduct[]>) => {
      state.requestProducts = action.payload;
      state.requestProductsLoading = false;
    },
    setRequestProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.requestProductsLoading = action.payload;
    },
    setRequestProductsPagination: (
      state,
      action: PayloadAction<PaginationInfo>,
    ) => {
      state.requestProductsPagination = action.payload;
    },
    setSelectedRequestId: (state, action: PayloadAction<string | null>) => {
      state.selectedRequestId = action.payload;
    },
    clearSelectedRequestId: (state) => {
      state.selectedRequestId = null;
    },
    setSelectedRequest: (
      state,
      action: PayloadAction<RequestProduct | null>,
    ) => {
      state.selectedRequest = action.payload;
    },
    setRequestCreateEditDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        openFor: "create" | "edit" | "view";
        editItemData?: RequestProduct;
      }>,
    ) => {
      state.requestCreateEditDialogOpen = action.payload;
    },
    clearRequestCreateEditDialogOpen: (state) => {
      state.requestCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },
    setIsRequestDeleteDialogOpen: (
      state,
      action: PayloadAction<{ open: boolean; deleteItemId?: string }>,
    ) => {
      state.isRequestDeleteDialogOpen = action.payload;
    },

    // Supplier Product actions
    setSupplierProducts: (state, action: PayloadAction<SupplierProduct[]>) => {
      state.supplierProducts = action.payload;
      state.supplierProductsLoading = false;
    },
    setSupplierProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.supplierProductsLoading = action.payload;
    },
    setSupplierProductsPagination: (
      state,
      action: PayloadAction<PaginationInfo>,
    ) => {
      state.supplierProductsPagination = action.payload;
    },
    setSelectedSupplierId: (state, action: PayloadAction<string | null>) => {
      state.selectedSupplierId = action.payload;
    },
    clearSelectedSupplierId: (state) => {
      state.selectedSupplierId = null;
    },
    setSelectedSupplierProduct: (
      state,
      action: PayloadAction<SupplierProduct | null>,
    ) => {
      state.selectedSupplierProduct = action.payload;
    },
    setSupplierProductCreateEditDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        openFor: "create" | "edit" | "view";
        editItemData?: SupplierProduct;
      }>,
    ) => {
      state.supplierProductCreateEditDialogOpen = action.payload;
    },
    clearSupplierProductCreateEditDialogOpen: (state) => {
      state.supplierProductCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },
    setIsSupplierProductDeleteDialogOpen: (
      state,
      action: PayloadAction<{ open: boolean; deleteItemId?: string }>,
    ) => {
      state.isSupplierProductDeleteDialogOpen = action.payload;
    },
  },
});

export const {
  // Category actions
  setCategories,
  setCategoriesLoading,
  setCategoriesPagination,
  setSelectedCategoryId,
  clearSelectedCategoryId,
  setSelectedCategory,
  setCategoryCreateEditDialogOpen,
  clearCategoryCreateEditDialogOpen,
  setIsCategoryDeleteDialogOpen,
  setProductCategoryCreateEditLoading,
  setCategoryCreateEditError,
  setProductCategoryCreateEditFieldErrors,
  setProductCategoryDeleteLoading,

  // Central Product actions
  setCentralProducts,
  setCentralProductsLoading,
  setCentralProductsPagination,
  setSelectedProductId,
  clearSelectedProductId,
  setSelectedProduct,
  setProductCreateEditDialogOpen,
  clearProductCreateEditDialogOpen,
  setIsProductDeleteDialogOpen,

  // Request Product actions
  setRequestProducts,
  setRequestProductsLoading,
  setRequestProductsPagination,
  setSelectedRequestId,
  clearSelectedRequestId,
  setSelectedRequest,
  setRequestCreateEditDialogOpen,
  clearRequestCreateEditDialogOpen,
  setIsRequestDeleteDialogOpen,

  // Supplier Product actions
  setSupplierProducts,
  setSupplierProductsLoading,
  setSupplierProductsPagination,
  setSelectedSupplierId,
  clearSelectedSupplierId,
  setSelectedSupplierProduct,
  setSupplierProductCreateEditDialogOpen,
  clearSupplierProductCreateEditDialogOpen,
  setIsSupplierProductDeleteDialogOpen,
} = productSlice.actions;

export default productSlice.reducer;
